import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  ApiBaseUrl,
  getAllEmployeeApi,
  removeEmployeeApi,
  updateEmployeeApi,
  addEmployeeApi,
} from "../utils/Constants";

// Helper function for config
const getAuthConfig = () => ({
  headers: {
    Authorization: localStorage.getItem("token"),
  },
});

// Fetch employee list
export const getEmployeeList = createAsyncThunk(
  "employee/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(ApiBaseUrl + getAllEmployeeApi, getAuthConfig());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch employee list."
      );
    }
  }
);

// Add employee
export const addEmployee = createAsyncThunk(
  "employee/add",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(ApiBaseUrl + addEmployeeApi, payload, getAuthConfig());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to add employee."
      );
    }
  }
);

// Edit employee
export const editEmployee = createAsyncThunk(
  "employee/edit",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.put(ApiBaseUrl + updateEmployeeApi, payload, getAuthConfig());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to update employee."
      );
    }
  }
);

// Delete employee
export const deleteEmployee = createAsyncThunk(
  "employee/delete",
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${ApiBaseUrl}${removeEmployeeApi}${employeeId}`,
        getAuthConfig()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to delete employee."
      );
    }
  }
);

const employeeSlice = createSlice({
  name: "employee",
  initialState: {
    employees: [],
    isLoading: false,
    isError: false,
    errorMessage: null,
  },
  reducers: {
    clearEmployeeData: (state) => {
      state.employees = [];
      state.isError = false;
      state.errorMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get
      .addCase(getEmployeeList.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getEmployeeList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.employees = action.payload.data;
      })
      .addCase(getEmployeeList.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Add
      .addCase(addEmployee.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(addEmployee.fulfilled, (state, action) => {
        state.isLoading = false;
        state.employees.push(action.payload.data);
      })
      .addCase(addEmployee.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Edit
      .addCase(editEmployee.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(editEmployee.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.employees.findIndex(emp => emp._id === action.payload.data._id);
        if (index !== -1) {
          state.employees[index] = action.payload.data;
        }
      })
      .addCase(editEmployee.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Delete
      .addCase(deleteEmployee.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        state.isLoading = false;
        state.employees = state.employees.filter(emp => emp._id !== action.meta.arg);
      })
      .addCase(deleteEmployee.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      });
  },
});

export const { clearEmployeeData } = employeeSlice.actions;
export default employeeSlice.reducer;
